/* eslint no-useless-computed-key: 0 */
import { DEV, STAGING, RATIO_PATTERN } from './config';
import {
  NUMBER_REM_SCALE_RAW,
  SIZE_MAX_WIDTH_MEDIA_LARGE_BASE,
  SIZE_MAX_WIDTH_MEDIA_MEDIUM_BASE,
  SIZE_MAX_WIDTH_MEDIA_MEDIUM_HERO,
  SIZE_MAX_WIDTH_MEDIA_SMALL_BASE,
  SIZE_MIN_WIDTH_MEDIA_X_LARGE_BASE,
  SIZE_MIN_WIDTH_MEDIA_LARGE_BASE,
  SIZE_MIN_WIDTH_MEDIA_MEDIUM_BASE,
  SIZE_MIN_WIDTH_MEDIA_SMALL_BASE,
  SIZE_MIN_WIDTH_MEDIA_LARGE_PRIZES_SECTION,
} from '../../design-tokens/build/dictionary';

/**
 * Log stuff only in developoment. util for adding a bunch of logs in dev and then flagging them off and on depending on what you're debugging
 *
 * @export
 * @param {String} prefix Prefix to add to all logs
 * @param {Boolean} enabled If this log is enabled
 * @param {...*} args The stuff you want to log
 */
export function devLog(prefix, enabled, ...args) { // eslint-disable-line
  const newLogs = [prefix].concat(args);

  if ((DEV || STAGING) && enabled) {
    console.log.apply(null, newLogs); // eslint-disable-line
  }
}

/**
 * Log stuff only in developoment. util for adding a bunch of logs in dev and then flagging them off and on depending on what you're debugging
 *
 * @export
 * @param {String} prefix Prefix to add to all logs
 * @param {Boolean} enabled If this log is enabled
 * @param {...*} args The stuff you want to log
 */
export function devError(prefix, enabled, ...args) { // eslint-disable-line
  const newLogs = [prefix].concat(args);

  if ((DEV || STAGING) && enabled) {
    console.error.apply(null, newLogs); // eslint-disable-line
  }
}

/**
 * Util from wrapping set timeout in a promise
 *
 * @export
 * @param {*} delay
 * @returns {Promise.<null>}
 */
export function delayPromise(delay) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delay);
  });
}
/**
 * Es lint shouting about assigning to function params below lines disabled.
 *
 * @param {Number} t - current time
 * @param {Number} s - start position
 * @param {Number} c - change in position
 * @param {Number} d - duration of change
 */
export function easeInOutQuad(time, startPos, posChange, duration) {
  let newTime = time / (duration / 2);

  if (newTime < 1) {
    return posChange / 2 * newTime * newTime + startPos;
  }
  newTime -= 1;
  return -posChange / 2 * (newTime * (newTime - 2) - 1) + startPos;
}

/**
* Scroll to a DOM element
* @param {*}
* @param {Element} element - The element that is being scrolled (default is the document);
* @param {Element} toElement - The element to scroll to.
* @param {number}  duration - How long the scrolling should take.
*/
export function scrollTo({ element = document.documentElement, target: toElement, duration }) {
  if (!element || !toElement || !duration) throw new Error('Missing required vars');
  const start = element.scrollTop;
  const to = toElement.getBoundingClientRect().top + start;
  const change = to - start;

  const increment = 10;
  let currentTime = 0;

  function animateScroll() {
    currentTime += increment;
    const pos = easeInOutQuad(currentTime, start, change, duration);
    element.scrollTop = pos;  // eslint-disable-line
    if (currentTime < duration) {
      requestAnimationFrame(animateScroll);
    }
  }
  animateScroll();
}

/**
 * Get the root scrolling element x-browser
 *
 * @export
 * @returns {DOMElement}
 */
export function getScrollRoot() {
  return document.scrollingElement || document.documentElement || document.body;
}

/**
 * Convert a token size into a number
 *
 * @export
 * @param {*} token
 * @param {boolean} [emToPixel=true] don em to pixel conversion
 * @returns {Number}
 */
export function getNumberFromToken(token, emToPixel = true) {
  return parseFloat(token) * (emToPixel && 10);
}

/**
 * Expand template (for share links)
 *
 * @export
 * @param {string} template
 * @param {*} tokens
 * @returns {String}
 */
export function expandTemplate(template, tokens) {
  return template.replace(/(?:\[\[(.*?)\]\])/g, (match, value) => encodeURIComponent(tokens[value]));
}

/**
 * Given rem value, return ems
 */
export function getBreakpointValueFromToken(valueInRems) {
  let val = valueInRems;
  if (typeof valueInRems === 'string') {
    val = parseFloat(valueInRems);
  }

  return `${val * NUMBER_REM_SCALE_RAW}em`;
}


export const GLOBAL_MEDIA = {
  ['x-large']:
    `(min-width: ${getBreakpointValueFromToken(SIZE_MIN_WIDTH_MEDIA_X_LARGE_BASE)})`,
  ['large']:
    `(min-width: ${getBreakpointValueFromToken(SIZE_MIN_WIDTH_MEDIA_LARGE_BASE)}) and (max-width: ${getBreakpointValueFromToken(SIZE_MAX_WIDTH_MEDIA_LARGE_BASE)})`,
  ['medium']:
    `(min-width: ${getBreakpointValueFromToken(SIZE_MIN_WIDTH_MEDIA_MEDIUM_BASE)}) and (max-width: ${getBreakpointValueFromToken(SIZE_MAX_WIDTH_MEDIA_MEDIUM_BASE)})`,
  ['small']:
    `(min-width: ${getBreakpointValueFromToken(SIZE_MIN_WIDTH_MEDIA_SMALL_BASE)}) and (max-width: ${getBreakpointValueFromToken(SIZE_MAX_WIDTH_MEDIA_SMALL_BASE)})`,
  ['medium-hero']:
    `(min-width: ${getBreakpointValueFromToken(SIZE_MIN_WIDTH_MEDIA_SMALL_BASE)}) and (max-width: ${getBreakpointValueFromToken(SIZE_MAX_WIDTH_MEDIA_MEDIUM_HERO)})`,
  ['large-prizes']:
    `(min-width: ${getBreakpointValueFromToken(SIZE_MIN_WIDTH_MEDIA_LARGE_PRIZES_SECTION)})`,
};


/**
 * Pull width and height from aspect ratio
 *
 * @export
 * @param {Strings} aspectRatioString
 * @returns
 */
export function getDimensionsFromAspectRatio(aspectRatioString) {
  const matches = aspectRatioString.match(RATIO_PATTERN);

  if (!matches) {
    return null;
  }
  const [, width, height] = matches;

  if (!width && !height) {
    return null;
  }

  return {
    width,
    height,
  };
}
